import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { EmptyState, PageHeader, SliderField } from "@/components/shared";
import { PAGE_HELP } from "@/components/help";
import { Activity, CircleDot, Dog, Plug, Trash2, Watch } from "lucide-react";

// Device catalog. Sync is manual for now: owners log readings from their device's app
// and Soula Paws folds them into stress/wellness tracking and AI context.
const DEVICE_TYPES = [
  {
    key: "apple_watch", name: "Apple Watch / Apple Health", icon: Watch, wearer: "human",
    blurb: "Track your own stress, heart rate, and sleep — because your calm shapes your pet's calm.",
    fields: [
      { key: "restingHeartRate", label: "Resting heart rate (bpm)", type: "number" },
      { key: "sleepHours", label: "Sleep last night (hours)", type: "number" },
    ],
  },
  {
    key: "oura_ring", name: "Oura Ring", icon: CircleDot, wearer: "human",
    blurb: "Bring in readiness, sleep, and HRV trends to see how your recovery affects the bond.",
    fields: [
      { key: "readinessScore", label: "Readiness score (0-100)", type: "number" },
      { key: "sleepHours", label: "Sleep last night (hours)", type: "number" },
    ],
  },
  {
    key: "smart_collar", name: "Smart Dog Collar (Fi, Whistle, Halo...)", icon: Dog, wearer: "pet",
    blurb: "Log your pet's activity, rest, and stress signals straight from their collar app.",
    fields: [
      { key: "activityMinutes", label: "Active minutes today", type: "number" },
      { key: "restHours", label: "Rest/sleep (hours)", type: "number" },
    ],
  },
];

const typeOf = (key) => DEVICE_TYPES.find((d) => d.key === key);

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [readings, setReadings] = useState([]);
  const [pets, setPets] = useState([]);
  const [connectOpen, setConnectOpen] = useState(null); // device type being connected
  const [logOpen, setLogOpen] = useState(null); // connected device being logged for
  const [label, setLabel] = useState("");
  const [petId, setPetId] = useState("");
  const [reading, setReading] = useState({});
  const [stress, setStress] = useState(4);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, r, p] = await Promise.all([
        api.get("/device-integrations?limit=20"),
        api.get("/device-readings?limit=10"),
        api.get("/pet-profiles"),
      ]);
      setDevices(d.data); setReadings(r.data); setPets(p.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    const t = connectOpen;
    if (t.wearer === "pet" && !petId) { toast.error("Choose which pet wears this collar."); return; }
    setBusy(true);
    try {
      await api.post("/device-integrations", {
        deviceType: t.key,
        name: label.trim() || t.name,
        wearer: t.wearer,
        ...(t.wearer === "pet" ? { petId } : {}),
        status: "connected",
        syncMode: "manual",
      });
      toast.success(`${t.name} connected. Log readings anytime — they feed wellness tracking and AI context.`);
      setConnectOpen(null); setLabel(""); setPetId("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const disconnect = async (d) => {
    if (!window.confirm(`Disconnect "${d.name}"?`)) return;
    try { await api.delete(`/device-integrations/${d.id}`); toast.success("Device disconnected."); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const saveReading = async () => {
    const t = typeOf(logOpen.deviceType);
    setBusy(true);
    try {
      await api.post("/device-readings", {
        deviceType: logOpen.deviceType,
        deviceId: logOpen.id,
        date: new Date().toISOString().slice(0, 10),
        ...(logOpen.petId ? { petId: logOpen.petId } : {}),
        stressScore: stress,
        ...Object.fromEntries(Object.entries(reading).filter(([, v]) => v !== "" && v !== undefined).map(([k, v]) => [k, Number(v)])),
      });
      toast.success("Reading saved — it now informs wellness tracking and AI recommendations.");
      setLogOpen(null); setReading({}); setStress(4);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const connectedKeys = new Set(devices.map((d) => d.deviceType));

  return (
    <div>
      <PageHeader title="Devices & Wearables" help={PAGE_HELP.devices} subtitle="Connect what you and your pet already wear — stress and wellness signals in one place." />

      {/* Available devices */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {DEVICE_TYPES.map((t) => {
          const Icon = t.icon;
          const connected = connectedKeys.has(t.key);
          return (
            <Card key={t.key} className="rounded-2xl h-full" data-testid={`device-type-card-${t.key}`}>
              <CardContent className="p-5 flex flex-col h-full">
                <div className="flex items-start justify-between">
                  <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
                  {connected && <Badge variant="secondary" className="border-0 bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]">Connected</Badge>}
                </div>
                <p className="font-semibold text-sm mt-3">{t.name}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex-1">{t.blurb}</p>
                <div className="mt-auto pt-3">
                  <Button size="sm" variant={connected ? "secondary" : "default"} className="rounded-lg w-full" onClick={() => { setConnectOpen(t); setLabel(""); setPetId(pets[0]?.id || ""); }} data-testid={`connect-device-button-${t.key}`}>
                    <Plug className="w-3.5 h-3.5 mr-1" /> {connected ? "Connect another" : "Connect"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">Readings are logged from your device's app for now — direct automatic sync is on our roadmap. Everything you log feeds wellness tracking and the AI's understanding of your pet.</p>

      {/* Connected devices */}
      <div className="mt-8">
        <p className="text-sm font-medium mb-3">Connected devices</p>
        {devices.length ? (
          <div className="space-y-2">
            {devices.map((d) => {
              const t = typeOf(d.deviceType);
              const pet = pets.find((p) => p.id === d.petId);
              return (
                <Card key={d.id} className="rounded-xl" data-testid={`connected-device-${d.id}`}>
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{t?.name || d.deviceType}{pet ? ` · worn by ${pet.name}` : " · worn by you"} · manual sync</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" className="rounded-lg" onClick={() => { setLogOpen(d); setReading({}); setStress(4); }} data-testid={`log-reading-button-${d.id}`}><Activity className="w-3.5 h-3.5 mr-1" /> Log reading</Button>
                      <Button size="sm" variant="ghost" className="rounded-lg text-destructive hover:text-destructive" onClick={() => disconnect(d)} data-testid={`disconnect-device-button-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <EmptyState icon={Watch} title="No devices connected yet" hint="Connect a wearable above to fold stress and wellness signals into your bond insights." />
        )}
      </div>

      {/* Recent readings */}
      {readings.length > 0 && (
        <div className="mt-8">
          <p className="text-sm font-medium mb-3">Recent readings</p>
          <div className="space-y-2">
            {readings.map((r) => {
              const t = typeOf(r.deviceType);
              const pet = pets.find((p) => p.id === r.petId);
              const metrics = Object.entries(r)
                .filter(([k, v]) => ["restingHeartRate", "sleepHours", "readinessScore", "activityMinutes", "restHours", "stressScore"].includes(k) && v !== null && v !== undefined)
                .map(([k, v]) => `${k.replace(/([A-Z])/g, " $1").toLowerCase()}: ${v}`)
                .join(" · ");
              return (
                <Card key={r.id} className="rounded-xl" data-testid={`device-reading-${r.id}`}>
                  <CardContent className="p-3.5 text-sm">
                    <p><span className="font-medium">{t?.name || r.deviceType}</span>{pet ? ` · ${pet.name}` : ""} · <span className="text-xs text-muted-foreground">{r.date}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">{metrics}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Connect dialog */}
      <Dialog open={!!connectOpen} onOpenChange={(o) => !o && setConnectOpen(null)}>
        <DialogContent data-testid="connect-device-dialog">
          <DialogHeader><DialogTitle>Connect {connectOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Label (optional)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={connectOpen?.wearer === "pet" ? "e.g. Biscuit's Fi collar" : "e.g. My Apple Watch"} data-testid="device-label-input" /></div>
            {connectOpen?.wearer === "pet" && (
              <div className="space-y-1.5">
                <Label>Worn by *</Label>
                <Select value={petId} onValueChange={setPetId}>
                  <SelectTrigger data-testid="device-pet-select"><SelectValue placeholder="Choose pet" /></SelectTrigger>
                  <SelectContent>{pets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">You'll log readings from your device's app for now — direct automatic sync is on our roadmap.</p>
          </div>
          <DialogFooter><Button onClick={connect} disabled={busy} className="rounded-xl" data-testid="device-connect-save-button">{busy ? "Connecting..." : "Connect device"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log reading dialog */}
      <Dialog open={!!logOpen} onOpenChange={(o) => !o && setLogOpen(null)}>
        <DialogContent data-testid="log-reading-dialog">
          <DialogHeader><DialogTitle>Log reading · {logOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(typeOf(logOpen?.deviceType)?.fields || []).map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}</Label>
                <Input type={f.type} value={reading[f.key] ?? ""} onChange={(e) => setReading((prev) => ({ ...prev, [f.key]: e.target.value }))} data-testid={`reading-input-${f.key}`} />
              </div>
            ))}
            <SliderField label={typeOf(logOpen?.deviceType)?.wearer === "pet" ? "Stress signals (from collar app)" : "Your stress level"} value={stress} onChange={setStress} testId="reading-stress-slider" />
          </div>
          <DialogFooter><Button onClick={saveReading} disabled={busy} className="rounded-xl" data-testid="reading-save-button">{busy ? "Saving..." : "Save reading"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
